// Modules
import * as THREE from 'three'

// Utils
import utils from '../../utils/utils'

const __HACK_GET_EXACT_HEIGHT_ACCORDING_FONT_SIZE = x => x * 1.4

const FALLBACK_FONT_FAMILY = "Arial, Helvetica, sans-serif"

const CYLINDER_RADIUS = 5


export default class Cylinder{
    /**
     * 
     * @param {Object[]} words
     * @param {String} words.text
     * @param {Number} words.fontSize
     * @param {Number} words.fontWeight
     * @param {Number} words.letterSpacing
     * @param {String} words.fontFamily
     * @param {Object} parameters
     * @param {Number} parameters.repeat
     * @param {Number} parameters.spaceBetween
     * @param {Number} parameters.spaceBetweenWord
     * @param {Boolean} parameters.reversed
     * @param {any} parameters.side
     * @param {Number} parameters.cylinderRadius
     * @param {Number} parameters.quality
     * @param {Function} parameters.customDrawing
     * @param {Number} parameters.radialSegments
     * @param {Number} parameters.anisotropy
     * @param {Boolean} parameters.nearestPow2
     * @param {*} parameters.minFilter
     * 
     */
    constructor(words, parameters = {}){
        
        /** Set parameters */
        // parameters.repeat = parameters.repeat && parameters.repeat > 1 ? parameters.repeat : 1
        parameters.spaceBetween = utils.isUndefined(parameters.spaceBetween) ? 0 : parameters.spaceBetween
        parameters.reversed = utils.isUndefined(parameters.reversed) ? false : parameters.reversed
        parameters.side = utils.isUndefined(parameters.side) ? THREE.DoubleSide : parameters.side
        parameters.cylinderRadius = utils.isUndefined(parameters.cylinderRadius) ? CYLINDER_RADIUS : parameters.cylinderRadius
        parameters.quality = utils.isUndefined(parameters.quality) ? (window.devicePixelRatio || 1) : parameters.quality
        parameters.radialSegments = utils.isUndefined(parameters.radialSegments) ? 32 : parameters.radialSegments
        parameters.nearestPow2 = utils.isUndefined(parameters.nearestPow2) ? true : parameters.nearestPow2
        parameters.anisotropy = utils.isUndefined(parameters.anisotropy) ? 1 : parameters.anisotropy

        this.parameters = parameters

        /** Public variables */
        this.mesh = new THREE.Mesh()

        // reference of this
        this.mesh.userData.instance = this
        
        this.canvas = null
        this.canvasContext = null

        this.canvasWidth = null
        this.canvasHeight = null

        this.wordsCoordinates = []

        // Init canvas & context
        this.createCanvas()
        this.createCanvasContext()

        this.words = this.formatWords(words)
        // We've got each word dimensions we can compute final canvas size
        this.computeCanvasDimensions()

        this.computeDrawParameters()

        this.drawRepeatedWords()

        this.buildCylinder()

        // @DEV
        // show cylinder helper
        // this.addCylinderHelper()

        // if reversed
        if(parameters.reversed){
            this.mesh.scale.x = -1
        }

    }

    /**
     * Publics
     */

    getMesh(){
        return this.mesh
    }

    destroy(){
        this.mesh.geometry.dispose()
        this.mesh.material.dispose()
        this.mesh.material.map.dispose()
    }

    pushNextTexture(){
        
    }

    /**
     * Privates
     */

    nearestPow2(x){

        if(!this.parameters.nearestPow2){
            return x 
        } else {
            return Math.pow( 2, Math.round( Math.log( x ) / Math.log( 2 ) ) )
        }
    }

    computeSumOfWordDimension(words){

        // only width
        let wordsDimensions = words.map(w => w.dimension.width)
        let sumWidthWords = 0

        // set sum of width
        if(wordsDimensions.length === 1){
            sumWidthWords = wordsDimensions[0]
        } else {
            sumWidthWords = wordsDimensions.reduce((acc, current) => acc + current)
        }

        return sumWidthWords
    }

    computeDrawParameters(){

        let textureWidth = this.canvas.width
        let sumWidthWords = this.computeSumOfWordDimension(this.words)
        let maxRepeat = Math.floor(textureWidth / sumWidthWords)
        maxRepeat = utils.isUndefined(this.parameters.repeat) ? maxRepeat : utils.limit(this.parameters.repeat, 1, maxRepeat)
        
        let rest = textureWidth - sumWidthWords * maxRepeat

        this.parameters.repeat = maxRepeat

        // we'll use same space between words and repeat
        if(utils.isUndefined(this.parameters.spaceBetweenWord)){

            let uniformSpaceBetween = rest / (maxRepeat * this.words.length)

            this.parameters.spaceBetweenWord = uniformSpaceBetween
            this.parameters.spaceBetweenRepeat = uniformSpaceBetween
        } else {
            this.parameters.spaceBetweenRepeat = (rest - this.parameters.spaceBetweenWord * (this.words.length - 1) * maxRepeat) / maxRepeat
        }
        
    }

    computeCanvasDimensions(){

        let scaler = this.parameters.quality

        let maxFontSize = Math.max(...this.words.map(w => w._.fontSize))


        let finalCircumference = this.parameters.cylinderRadius * 2 * Math.PI * maxFontSize

        this.canvasWidth = this.nearestPow2(finalCircumference)
        this.canvasHeight = this.nearestPow2(__HACK_GET_EXACT_HEIGHT_ACCORDING_FONT_SIZE(maxFontSize))

        // apply scaled dimension to canvas
        this.canvas.width = this.canvasWidth * scaler
        this.canvas.height = this.canvasHeight * scaler


        // @DEV
        // this.showDevCanvas()

    }

    formatWords(words){

        let scaler = this.parameters.quality

        return words.map(w => {

            let fontSize = w.fontSize * scaler

            let font = `${w.fontWeight} ${fontSize}px ${w.fontFamily}, ${FALLBACK_FONT_FAMILY}`
            let letterSpacing = w.letterSpacing || 0

            return {
                _: w,
                text: w.text,
                font: font,
                letterSpacing,
                dimension: {
                    width: this.getComputedDimension(w.text, font, letterSpacing).width,
                    height: __HACK_GET_EXACT_HEIGHT_ACCORDING_FONT_SIZE(fontSize)
                }
            }
        })
    }

    buildCylinder(){
        let cylinder1Texture = new THREE.CanvasTexture(this.canvas)

        cylinder1Texture.wrapT = THREE.ClampToEdgeWrapping
        cylinder1Texture.wrapS = THREE.ClampToEdgeWrapping
        cylinder1Texture.repeat.set(1, 1)
        cylinder1Texture.magFilter = THREE.LinearFilter
        cylinder1Texture.minFilter = this.parameters.minFilter || THREE.LinearMipmapLinearFilter

        cylinder1Texture.anisotropy = this.parameters.anisotropy

        let radius = this.parameters.cylinderRadius
        let height = this.canvasHeight * (2 * Math.PI * radius) / this.canvasWidth
        let radialSegments = this.parameters.radialSegments
        let heightSegments = 1

        this.mesh.geometry = new THREE.CylinderBufferGeometry(
            radius,
            radius,
            height,
            radialSegments,
            heightSegments,
            true, // openEnded
        )
        this.mesh.material = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            side: this.parameters.side,
            // wireframe: true,
            map: cylinder1Texture,
            alphaTest: .5,
            // transparent: true, 
        })
    }

    createCanvas(){
        this.canvas = document.createElement("canvas")
        this.canvas.width = 512
        this.canvas.height = 256

        // set styles
        Object.assign(this.canvas.style, {

            // position: "absolute",
            // width: "0px",
            // height: "0px",
        })

        document.getElementById("dev-canvas-ctn").appendChild(this.canvas)
    }

    showDevCanvas(){
        // @DEV
        // show canvas
        Object.assign(this.canvas.style, {
            position: "static",
            display: "block",
            border: "2px solid orange",
            width: 1000 + "px",
            height: (1000 * this.canvas.height / this.canvas.width) + "px",
            left: "0",
            bottom: "0",
            zIndex: "1000000",
        })
    }

    createCanvasContext(){
        this.canvasContext = this.canvas.getContext("2d")
    }

    drawRepeatedWords(){

        this.canvasContext.fillStyle = "rgba(0, 0, 0, 0)"
        this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height)
        
        // init context
        this.canvasContext.fillStyle = "white"

        // loop draw
        let offset = 0

        let wordCount = this.words.length
        let repeatCount = this.parameters.repeat

        if(this.parameters.repeat === 0){
            console.warn("BE CAREFUL ! You will draw with repeat = 0", this)
        }
        for(let r = 0; r < repeatCount; r++){

            for(let w = 0; w < wordCount; w++){

                let isLastWord = w === wordCount - 1

                let {font, text, dimension, letterSpacing, _: {fontWeight}} = this.words[w]

                // init context
                this.canvas.style.letterSpacing = letterSpacing + "px" // must be applied before modifying canvas contex
                this.canvasContext.font = font

                // allow a custom drawing (custom offset)
                let _offset = this.parameters.customDrawing ? this.parameters.customDrawing(w, r, offset, dimension) : offset
                let offsetY = .5 * this.canvas.height + dimension.height * .25
                // draw

                let wordStackingCount = 1

                // force whiteness & fix thick letter bad quality
                // if(wordStackingCount > 1){
                //     this.canvasContext.globalAlpha = 10
                // }

                for(let wordStacking = 0; wordStacking < wordStackingCount; wordStacking++){
                    this.canvasContext.fillText(
                        text, 
                        _offset,
                        offsetY
                    )
                }

                this.wordsCoordinates.push({
                    index: w,
                    repeat: r,

                    offset: _offset,
                    offsetY: offsetY,

                    dimension,

                    from: _offset / this.canvas.width,
                    to: (_offset + dimension.width) / this.canvas.width,

                    word: this.words[w]
                })
                
                // add offset on x-axis
                offset += dimension.width + (!isLastWord ? this.parameters.spaceBetweenWord : 0)
            }

            offset += this.parameters.spaceBetweenRepeat

        }
    }
    
    getComputedDimension(text, font, letterSpacing){
        
        this.canvas.style.letterSpacing = letterSpacing + "px"
        this.canvasContext.font = font

        let measure = this.canvasContext.measureText(text)
        this.canvasContext.font = null

        return measure
    }

    addCylinderHelper(){
        this.mesh.add(
            new THREE.Mesh(
                this.mesh.geometry,
                new THREE.MeshBasicMaterial({
                    color: "green",
                    wireframe: true
                })
            )
        )
    }
}